/**
 * Tiếng báo khi chuông có tin mới (CEO chốt 1a · 2a · 3a, 21/10/2026): mọi tin
 * mới đều kêu, mỗi máy tự tắt được bằng nút loa trong hộp chuông (mặc định BẬT).
 * Tiếng do CEO chọn từ kho Mixkit (giấy phép miễn phí, dùng thương mại được):
 * tin thường kêu "Chuông cửa", tin TIỀN VỀ kêu "Tiếng đúng rồi" để sale đứng
 * quầy nghe là biết tiền đã vào mà không cần nhìn màn (CEO chốt 1a, 21/10/2026).
 *
 * Ba chỗ dễ hỏng mà file này gánh:
 *  - Trình duyệt chặn phát tiếng khi người dùng chưa chạm trang lần nào — phải
 *    "mở khoá" bộ phát ngay lần chạm đầu tiên, không thì tin tới là im.
 *  - Mở ERP nhiều tab thì tab nào cũng thấy số đỏ tăng — chỉ được kêu MỘT lần.
 *  - Lần tải đầu (mở trang đã có sẵn 3 tin cũ) không phải tin mới — không kêu.
 */

import { useEffect, useRef } from "react";

import chuongCuaUrl from "@/assets/sounds/chuong-cua.mp3";
import tiengDungRoiUrl from "@/assets/sounds/tieng-dung-roi.mp3";

export type SoundKind = "main" | "money";

const SOUND_URL: Record<SoundKind, string> = {
  main: chuongCuaUrl,
  money: tiengDungRoiUrl,
};

const MUTE_KEY = "gnpf.notifSound.muted";
const LAST_KEY = "gnpf.notifSound.last";

/**
 * Hai tab cùng thấy một lần tăng cách nhau tối đa một nhịp hỏi (60 giây); tab
 * ẩn bị trình duyệt hãm nhịp nên có thể trễ gần gấp đôi. Trong khoảng này, tab
 * thấy số KHÔNG cao hơn số đã kêu thì im.
 */
const SAME_RISE_WINDOW_MS = 150_000;

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Chế độ ẩn danh / bị chặn lưu: vẫn kêu được, chỉ không nhớ giữa các tab.
  }
}

export function isSoundMuted(): boolean {
  return read(MUTE_KEY) === "1";
}

export function setSoundMuted(muted: boolean): void {
  write(MUTE_KEY, muted ? "1" : "0");
}

let ctx: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor =
    typeof window === "undefined"
      ? undefined
      : (window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

let primed = false;

/**
 * Mở khoá bộ phát ở lần chạm/gõ phím đầu tiên — sau đó tin tới lúc nào cũng
 * kêu được dù người dùng không đụng vào trang nữa.
 */
export function primeSound(): void {
  if (primed || typeof window === "undefined") return;
  primed = true;
  const unlock = () => {
    const c = audioContext();
    if (c && c.state === "suspended") void c.resume().catch(() => {});
    if (c) void loadSounds(c);
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("keydown", unlock, true);
  };
  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
}

const buffers: Partial<Record<SoundKind, AudioBuffer>> = {};
let loaded: Promise<void> | null = null;

/**
 * Nạp sẵn hai tệp tiếng vào bộ phát (giải mã một lần). Phát bằng bộ phát đã mở
 * khoá chứ không bằng thẻ `<audio>`: điện thoại chặn thẻ `<audio>` tự phát khi
 * tin tới mà người dùng không vừa chạm màn.
 */
function loadSounds(c: AudioContext): Promise<void> {
  loaded ??= Promise.all(
    (Object.keys(SOUND_URL) as SoundKind[]).map((kind) =>
      fetch(SOUND_URL[kind])
        .then((r) => r.arrayBuffer())
        .then((data) => c.decodeAudioData(data))
        .then((buf) => {
          buffers[kind] = buf;
        })
        .catch(() => {
          // Tải hỏng thì vẫn còn tiếng "ting ting" tự tạo — tin tới không bị im.
        }),
    ),
  ).then(() => undefined);
  return loaded;
}

/**
 * Phát tiếng đã chọn. Tệp chưa nạp xong thì chờ nạp (lần đầu bấm bật loa); nạp
 * hỏng thì kêu "ting ting" tự tạo.
 */
export function playDing(kind: SoundKind = "main"): void {
  const c = audioContext();
  if (!c) return;
  if (c.state === "suspended") void c.resume().catch(() => {});
  if (buffers[kind]) {
    playBuffer(c, buffers[kind]);
    return;
  }
  void loadSounds(c).then(() => {
    const buf = buffers[kind];
    if (buf) playBuffer(c, buf);
    else playSynth(c);
  });
}

function playBuffer(c: AudioContext, buf: AudioBuffer): void {
  const src = c.createBufferSource();
  src.buffer = buf;
  src.connect(c.destination);
  src.start();
}

function playSynth(c: AudioContext): void {
  const start = c.currentTime + 0.02;
  for (const at of [start, start + 0.16]) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1568, at);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.25, at + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.35);
    osc.connect(gain).connect(c.destination);
    osc.start(at);
    osc.stop(at + 0.4);
  }
}

type LastRing = { total: number; at: number };

function lastRing(): LastRing | null {
  const raw = read(LAST_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as LastRing;
    return typeof v.total === "number" && typeof v.at === "number" ? v : null;
  } catch {
    return null;
  }
}

/**
 * Số đỏ vừa tăng lên `total`: kêu nếu tab khác chưa kêu cho đúng lần tăng này.
 * Trả `true` khi tab này giành được lượt kêu.
 */
export function claimRing(total: number, now = Date.now()): boolean {
  const last = lastRing();
  if (last && total <= last.total && now - last.at < SAME_RISE_WINDOW_MS) return false;
  write(LAST_KEY, JSON.stringify({ total, at: now }));
  return true;
}

/**
 * Người dùng vừa đọc tin ở tab ĐANG XEM (số đỏ giảm) — hạ mốc để tin kế tiếp
 * vẫn kêu dù tới ngay sau đó. Tab ẩn thấy số giảm muộn thì không đụng mốc, kẻo
 * xoá mất dấu tab kia vừa kêu rồi kêu trùng.
 */
export function noteRead(total: number, now = Date.now()): void {
  write(LAST_KEY, JSON.stringify({ total, at: now }));
}

/**
 * Theo dõi số chưa đọc; số tăng so với lần trước thì kêu (trừ khi đã tắt loa).
 * `moneyOrderIds` = đơn đang có tin tiền về chưa đọc: có đơn MỚI lọt vào danh
 * sách đó trong lần tăng này thì kêu tiếng tiền về.
 */
export function useDingOnNewNotification(
  total: number | undefined,
  moneyOrderIds: readonly string[] | undefined,
): void {
  const prev = useRef<{ total: number; money: ReadonlySet<string> } | undefined>(undefined);

  useEffect(() => {
    primeSound();
  }, []);

  // Khoá theo NỘI DUNG danh sách, không theo mảng: mỗi lượt hỏi máy chủ trả mảng mới.
  const moneyKey = (moneyOrderIds ?? []).join(",");

  useEffect(() => {
    if (total === undefined) return;
    const money = new Set(moneyKey ? moneyKey.split(",") : []);
    const before = prev.current;
    prev.current = { total, money };
    if (before === undefined) return;
    if (total > before.total) {
      const moneyIn = [...money].some((id) => !before.money.has(id));
      if (!isSoundMuted() && claimRing(total)) playDing(moneyIn ? "money" : "main");
    } else if (total < before.total && document.visibilityState === "visible") {
      noteRead(total);
    }
  }, [total, moneyKey]);
}
